<script setup lang="ts">
import type { Component } from 'vue'
import Spinner from '@taler/ui/Spinner.vue'

/**
 * A button. `primary` is the one action on a surface, in --color-action; `secondary` and `quiet`
 * are everything else. `as` renders it as another element, e.g. a RouterLink.
 */
const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'quiet'
    size?: 'md' | 'lg'
    as?: string | Component
    loading?: boolean
  }>(),
  { variant: 'primary', size: 'md', as: 'button', loading: false },
)

function blockWhileLoading(event: MouseEvent): void {
  if (!props.loading) return
  event.preventDefault()
  event.stopImmediatePropagation()
}

const VARIANT_CLASSES = {
  primary:
    'bg-[color:var(--color-action)] text-[color:var(--color-action-text)] hover:bg-[color:var(--color-action-hover)] disabled:hover:bg-[color:var(--color-action)] aria-disabled:hover:bg-[color:var(--color-action)]',
  secondary:
    'border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-raised)] disabled:hover:bg-[color:var(--color-surface)] aria-disabled:hover:bg-[color:var(--color-surface)]',
  quiet:
    'text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface-sunken)] hover:text-[color:var(--color-text)] disabled:hover:bg-transparent disabled:hover:text-[color:var(--color-text-muted)] aria-disabled:hover:bg-transparent aria-disabled:hover:text-[color:var(--color-text-muted)]',
} as const
</script>

<template>
  <component
    :is="as"
    :type="as === 'button' ? 'button' : undefined"
    :data-variant="variant"
    :aria-busy="loading || undefined"
    :aria-disabled="loading || undefined"
    @click.capture="blockWhileLoading"
    class="inline-flex items-center justify-center gap-[var(--space-tight)] whitespace-nowrap rounded-[var(--radius-control)] text-[length:var(--text-step-3)] font-medium transition-colors duration-[var(--motion-fast)] disabled:cursor-not-allowed disabled:opacity-60 aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
    :class="[
      VARIANT_CLASSES[variant],
      size === 'lg'
        ? 'min-h-[var(--density-control-lg)] px-[var(--space-block)]'
        : 'min-h-[var(--density-target-min)] px-[var(--space-cell)] py-[var(--space-inline)]',
    ]"
  >
    <Spinner v-if="loading" />
    <slot />
  </component>
</template>
